// src/antviews/components/system/PanelHd3.tsx

// 三列的硬盘管理界面

import React from 'react';
import axios from 'axios';
import { Button, Card, Divider, Table, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { DataApi, SystemApi } from '../../../services/Api';
import { ROLE } from '../../../services/Macro';
import { ModalDelCsvBeforeDate } from './ModalDelCsvBeforeDate';
import { ModalDelLogBeforeDate } from './ModalDelLogBeforeDate';
import { ModalDelSdkLogBeforeDate } from './ModalDelSdkLogBeforeDate';

const SHOW_COL = ['Filesystem', 'Size', 'Used', 'Avail', 'Use%'];

interface PanelHdProps {
    trans: (key: string) => string;
}

interface DiskRow {
    key: React.Key;
    values: string[];
}

/**
 * 硬盘概览行: 项目 / 大小 / 占比
 * item 为多语言 key, 需通过 t() 查询显示文案
 */
interface DiskOverviewRow {
    item: string;
    size: unknown;
    proportion: unknown;
}

interface PanelHdState {
    list: DiskRow[];
    mainDisk: DiskOverviewRow[];
    disk1: DiskOverviewRow[];
    isModalDelCsvOpen: boolean;
    isModalDelLogOpen: boolean;
    isModalDelSdkLogOpen: boolean;
    loading: boolean;
    deleting: boolean;
}

export default class PanelHd3 extends React.Component<PanelHdProps, PanelHdState> {
    state: PanelHdState = {
        list: [],
        mainDisk: [],
        disk1: [],
        isModalDelCsvOpen: false,
        isModalDelLogOpen: false,
        isModalDelSdkLogOpen: false,
        loading: false,
        deleting: false,
    };

    updateInfo = (info: any) => {
        console.log('HD info =', info);

        const tableHead: string[] = Array.isArray(info?.tableHead) ? info.tableHead : [];
        const tableData: unknown[][] = Array.isArray(info?.tableData) ? info.tableData : [];
        const colIdxList = SHOW_COL.map((name) => tableHead.indexOf(name));

        const list: DiskRow[] = tableData.map((data, rowIdx) => ({
            key: rowIdx,
            values: colIdxList.map((idx) => (idx >= 0 ? String(data[idx] ?? '') : '')),
        }));

        // 兼容 overview / overview3 两种字段名
        const rawOverview = Array.isArray(info?.overview)
            ? info.overview
            : Array.isArray(info?.overview3)
                ? info.overview3
                : [];

        // 兼容两种数据形态:
        // 1) [{ item, size, proportion }, ...]
        // 2) { hdTotal: "78G", hdUsed: "59G", ... }  (旧对象形式)
        let overview: DiskOverviewRow[];
        if (rawOverview.length && typeof rawOverview[0] === 'object' && 'item' in rawOverview[0]) {
            overview = rawOverview as DiskOverviewRow[];
        } else if (rawOverview.length === 0 && info?.overview && typeof info.overview === 'object') {
            // 旧对象形式: 键值对拉平成 item/size/proportion 三列
            const entries = Object.entries(info.overview as Record<string, unknown>);
            overview = entries.map(([key, val], i) => {
                // 占比 key 单独成行
                if (/Proportion/i.test(key)) {
                    return { item: key, size: '-', proportion: val };
                }
                // 大小 key: 往后找一个同前缀的 Proportion
                const prefix = key.replace(/Size|Total|Used|Avail/i, '');
                const propKey = entries.find(([k]) => k.startsWith(prefix) && /Proportion/i.test(k))?.[0];
                const propVal = propKey ? (info.overview as any)[propKey] : '-';
                return { item: key, size: val, proportion: propVal };
            });
        } else {
            overview = rawOverview as DiskOverviewRow[];
        }

        const MAIN_COUNT = 5; // 主硬盘 5 项, 按实际后端顺序调整
        const mainDisk = overview.slice(0, MAIN_COUNT);
        const disk1 = overview.slice(MAIN_COUNT);

        this.setState({ list, mainDisk, disk1 });
    };

    refreshInfo = async () => {
        const lang = localStorage.getItem('lang') || 'zh_CN';
        this.setState({ loading: true });

        try {
            const response = await axios.post(SystemApi.GET_HD_STATUS, {
                lang: lang.split('_')[0],
            });

            if (response.data?.data) {
                this.updateInfo(response.data.data);
            }
        } catch (error) {
            console.error('PanelHd.refreshInfo()', error);
        } finally {
            this.setState({ loading: false });
        }
    };

    openDelCsvModal = () => {
        this.setState({ isModalDelCsvOpen: true });
    };

    openDelLogModal = () => {
        this.setState({ isModalDelLogOpen: true });
    };

    openDelSdkLogModal = () => {
        this.setState({ isModalDelSdkLogOpen: true });
    };

    /**
     * 确认删除 CSV 文件.
     * @param date 
     */
    confirmDeleteCsv = async (date: string) => {
        const [token, lang, role] = this.getAccountInfo();

        this.setState({ isModalDelCsvOpen: false, deleting: true });

        try {
            await axios.post(DataApi.DEL_CSV_BEFORE, {
                role,
                token,
                lang: lang.split('_')[0],
                time: date,
            });
        } catch (error) {
            console.error('PanelHd.handleSelectDateOk()', error);
        } finally {
            this.setState({ deleting: false });
        }
    };

    /**
     * 放弃删除 CSV 文件.
     */
    cancelDeteleCsv = () => {
        this.setState({ isModalDelCsvOpen: false });
    };

    /**
     * 确认删除 HM 日志文件.
     * @param date 
     */
    confirmDeleteLog = async (date: string) => {
        const [token, lang, role] = this.getAccountInfo();

        this.setState({ isModalDelLogOpen: false, deleting: true });

        try {
            await axios.post(DataApi.DEL_LOG_BEFORE, {
                role,
                token,
                lang: lang.split('_')[0],
                time: date,
                logType: 'hm',
            });
        } catch (error) {
            console.error('PanelHd.confirmDeleteLog()', error);
        } finally {
            this.setState({ deleting: false });
        }
    };

    /**
     * 确认删除 SDK 日志文件.
     * @param date 
     */
    confirmDeleteSdkLog = async (date: string) => {
        const [token, lang, role] = this.getAccountInfo();

        this.setState({ isModalDelSdkLogOpen: false, deleting: true });

        try {
            await axios.post(DataApi.DEL_LOG_BEFORE, {
                role,
                token,
                lang: lang.split('_')[0],
                time: date,
                logType: 'sdk',
            });
        } catch (error) {
            console.error('PanelHd.confirmDeleteSdkLog()', error);
        } finally {
            this.setState({ deleting: false });
        }
    };

    /**
     * 放弃删除 HM 日志文件.
     */
    cancelDeteleLog = () => {
        this.setState({ isModalDelLogOpen: false });
    };

    /**
     * 放弃删除 SDK 日志文件.
     */
    cancelDeteleSdkLog = () => {
        this.setState({ isModalDelSdkLogOpen: false });
    };

    getAccountInfo = (): [string, string, string | number] => {
        const token = localStorage.getItem('token') || '';
        const lang = localStorage.getItem('lang') || 'zh_CN';
        const role = localStorage.getItem('role') || String(ROLE.SUPER);
        return [token, lang, role];
    };

    /**
     * 渲染硬盘概览表: 表头为 项目 / 大小 / 占比, 三列.
     * item 字段作为多语言 key, 经 t() 转换后显示.
     */
    renderOverview = (title: string, rows: DiskOverviewRow[]) => {
        const { trans: t } = this.props;

        const columns: ColumnsType<DiskOverviewRow> = [
            {
                title: t('item'),
                dataIndex: 'item',
                key: 'item',
                render: (value: string) => t(value),
            },
            {
                title: t('size'),
                dataIndex: 'size',
                key: 'size',
                render: (value: unknown) => String(value ?? ''),
            },
            {
                title: t('proportion'),
                dataIndex: 'proportion',
                key: 'proportion',
                render: (value: unknown) => String(value ?? ''),
            },
        ];

        return (
            <>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>{t(title)}</div>
                <Table<DiskOverviewRow>
                    rowKey={(record, index) => `${record.item}-${index}`}
                    size="small"
                    bordered
                    columns={columns}
                    dataSource={rows}
                    pagination={false}
                />
            </>
        );
    };

    render() {
        const { trans: t } = this.props;
        const { list, mainDisk, disk1, isModalDelCsvOpen, isModalDelLogOpen, isModalDelSdkLogOpen, loading, deleting } = this.state;

        const columns: ColumnsType<DiskRow> = SHOW_COL.map((name, index) => ({
            title: t(name),
            key: name,
            render: (_value, record) => record.values[index],
        }));

        return (
            <>
                <ModalDelCsvBeforeDate
                    isModalOpen={isModalDelCsvOpen}
                    handleOk={this.confirmDeleteCsv}
                    handleCancel={this.cancelDeteleCsv}
                />
                <ModalDelLogBeforeDate
                    isModalOpen={isModalDelLogOpen}
                    handleOk={this.confirmDeleteLog}
                    handleCancel={this.cancelDeteleLog}
                />
                <ModalDelSdkLogBeforeDate
                    isModalOpen={isModalDelSdkLogOpen}
                    handleOk={this.confirmDeleteSdkLog}
                    handleCancel={this.cancelDeteleSdkLog}
                />

                <Card
                    title={t('hdMgmt')}
                    extra={
                        <Button
                            type="text"
                            icon={<ReloadOutlined />}
                            loading={loading}
                            onClick={this.refreshInfo}
                        >
                            {t('refresh')}
                        </Button>
                    }
                >
                    {this.renderOverview('mainDisk', mainDisk)}

                    <Divider />

                    {this.renderOverview('disk1', disk1)}

                    <Space style={{ marginTop: 16 }}>
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            loading={deleting}
                            onClick={this.openDelCsvModal}
                        >
                            {t('delete-csv')}
                        </Button>
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            loading={deleting}
                            onClick={this.openDelLogModal}
                        >
                            {t('delete-hm-log')}
                        </Button>
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            loading={deleting}
                            onClick={this.openDelSdkLogModal}
                        >
                            {t('delete-sdk-log')}
                        </Button>
                    </Space>

                    <Divider />

                    <Table<DiskRow>
                        rowKey="key"
                        size="small"
                        bordered
                        columns={columns}
                        dataSource={list}
                        pagination={{ pageSize: 10, showSizeChanger: false }}
                        scroll={{ x: 'max-content' }}
                    />
                </Card>
            </>
        );
    }
}

/**

// 硬盘数据组织形式

overview3 = [
    // 主硬盘信息
    { item: "hdTotal", size: "78G", proportion: "100%" },
    { item: "hdUsed",  size: "59G", proportion: "80%"  },
    { item: "hdAvail", size: "19G", proportion: "20%"  },
    // 附加硬盘信息
    ...
]

 */